'use strict'

// Stored ids are the component.type values already saved on a page.
// Labels and descriptions are what the create-component picker shows.

const COMPONENT_TYPE_CATALOG = [
  {
    id: 'table',
    label: 'Table',
    group: 'List records',
    icon: 'table',
    needsSpreadsheet: true,
    description: 'Rows and columns. Use this when people need to scan, sort, and compare many records.'
  },
  {
    id: 'cards',
    label: 'Cards',
    group: 'List records',
    icon: 'address-card',
    needsSpreadsheet: true,
    description: 'One card for each record, sitting side by side and wrapping to the next line.'
  },
  {
    id: 'card',
    label: 'Card',
    group: 'Show one record',
    icon: 'id-card',
    needsSpreadsheet: true,
    description: 'A read-only detail of the one record that opened the page. The fields are laid out like a card and cannot be edited.'
  },
  {
    id: 'calendar',
    label: 'Calendar',
    group: 'List records',
    icon: 'calendar-days',
    needsSpreadsheet: true,
    description: 'Places each record on the day of a date field.'
  },
  {
    id: 'maps',
    label: 'Map',
    group: 'List records',
    icon: 'map-location-dot',
    needsSpreadsheet: true,
    description: 'A marker for each record, from an address or from latitude and longitude.'
  },
  {
    id: 'carousel',
    label: 'Carousel',
    group: 'List records',
    icon: 'images',
    needsSpreadsheet: true,
    description: 'Slides through records. Each slide can show a caption, text, and an image.'
  },
  {
    id: 'pivot',
    label: 'Pivot table',
    group: 'Summarize',
    icon: 'table-cells',
    needsSpreadsheet: true,
    description: 'Groups records into rows and columns, with a count or total in each cell.'
  },
  {
    id: 'chart',
    label: 'Chart',
    group: 'Summarize',
    icon: 'chart-column',
    needsSpreadsheet: true,
    description: 'A bar, line, pie, or other graph of counts, sums, or averages.'
  },
  {
    id: 'form',
    label: 'Form',
    group: 'Collect input',
    icon: 'pen-to-square',
    needsSpreadsheet: true,
    description: 'Fields that create or edit one record.'
  },
  {
    id: 'login',
    label: 'Login',
    group: 'Collect input',
    icon: 'right-to-bracket',
    needsSpreadsheet: true,
    description: 'A sign-in form that checks a text column and a password column.'
  },
  {
    id: 'registration',
    label: 'Registration form',
    group: 'Collect input',
    icon: 'user-plus',
    needsSpreadsheet: true,
    description: 'Creates an account from a text column and a password column, then signs that person in. It can also set up an authenticator.'
  },
  {
    id: 'menu',
    label: 'Menu',
    group: 'Page content',
    icon: 'bars',
    needsSpreadsheet: true,
    description: 'Navigation links to pages in this app.'
  },
  {
    id: 'richtext',
    label: 'Rich text',
    group: 'Page content',
    icon: 'file-lines',
    needsSpreadsheet: false,
    description: 'A document that is the same for every visitor. It does not read a spreadsheet.'
  },
  {
    id: 'staticimage',
    label: 'Static image',
    group: 'Page content',
    icon: 'image',
    needsSpreadsheet: false,
    description: 'One picture stored on the view. Every visitor sees the same file.'
  },
  {
    id: 'map',
    label: 'Map',
    group: 'Other',
    icon: 'map',
    needsSpreadsheet: true,
    description: 'Stored as map. Live markers are drawn by the Map card in List records, stored as maps.'
  },
  {
    id: 'custom',
    label: 'Custom',
    group: 'Other',
    icon: 'puzzle-piece',
    needsSpreadsheet: true,
    description: 'Write the HTML for this view yourself. A wizard starts you from an example and names the fields you can use.'
  }
]

const STORED_TYPE_IDS = [
  'table',
  'pivot',
  'cards',
  'card',
  'form',
  'login',
  'registration',
  'menu',
  'maps',
  'calendar',
  'map',
  'custom',
  'carousel',
  'chart',
  'richtext',
  'staticimage'
]

function componentTypeCatalog () {

  return COMPONENT_TYPE_CATALOG.map((entry) => Object.assign({}, entry))
}

function componentTypeById (id) {

  const found = COMPONENT_TYPE_CATALOG.find((entry) => entry.id === id)

  return found ? Object.assign({}, found) : null
}

function componentTypeNeedsSpreadsheet (id) {

  if (!id) return false

  const entry = componentTypeById(id)

  if (!entry) return id !== 'richtext' && id !== 'staticimage'

  return entry.needsSpreadsheet
}

function filterComponentTypes (query) {

  const needle = String(query || '').trim().toLowerCase()

  if (!needle) return componentTypeCatalog()

  return componentTypeCatalog().filter((entry) => {

    const haystack = [entry.id, entry.label, entry.group, entry.description].join(' ').toLowerCase()

    return haystack.includes(needle)
  })
}

function componentTypeGroups (types) {

  const groups = []
  const index = new Map()

  for (const entry of types || []) {

    let group = index.get(entry.group)

    if (!group) {

      group = { id: entry.group, label: entry.group, types: [] }
      index.set(entry.group, group)
      groups.push(group)
    }

    group.types.push(entry)
  }

  return groups
}

module.exports = {
  STORED_TYPE_IDS,
  componentTypeCatalog,
  componentTypeById,
  componentTypeNeedsSpreadsheet,
  filterComponentTypes,
  componentTypeGroups
}
